import { Box, Typography, Fade, Button } from "@mui/material";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { translate } from "../../app/utils";
import AutoStoriesIcon from "@mui/icons-material/AutoStories";

const BookCard = ({ books, orientation = "row" }) => {
  const navigate = useNavigate();
  const [language] = useState(
    localStorage.getItem("currentLanguage")?.toUpperCase() || "EN",
  );

  if (!books || books.length === 0) {
    return (
      <Box sx={{ width: "100%", textAlign: "center", py: 8, opacity: 0.6 }}>
        <AutoStoriesIcon
          sx={{ fontSize: 64, mb: 2, color: "text.secondary" }}
        />
        <Typography variant="h6" color="text.secondary">
          No related books found.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr",
          sm: "repeat(2, 1fr)",
          md: "repeat(3, 1fr)",
          lg: "repeat(3, 1fr)",
        },
        gap: 4,
        mt: 4,
      }}
    >
      {books.map((book, index) => (
        <Fade
          in={true}
          timeout={300 + index * 100}
          key={`book-card-${book.UPLOAD_ID || book.ID || index}`}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              height: 220,
              bgcolor: "background.paper",
              borderRadius: 3,
              overflow: "hidden",
              boxShadow: "0 4px 10px rgba(0,0,0,0.03)",
              transition: "all 0.18s ease !important",
              border: "1px solid",
              borderColor: "divider",
              cursor: "pointer",
              "&:hover": {
                transform: "translateY(-4px)",
                boxShadow: "0 12px 24px rgba(0,0,0,0.08)",
                "& .cover-image": {
                  transform: "scale(1.1)",
                },
                "& .read-more-box": {
                  opacity: 1,
                  pointerEvents: "auto",
                },
              },
            }}
            onClick={() => {
              navigate("/books/details", { state: { book: { ...book } } });
            }}
          >
            {/* Left side: Cover Image */}
            <Box
              sx={{
                width: "35%",
                height: "100%",
                overflow: "hidden",
                position: "relative",
              }}
            >
              <Box
                className="cover-image"
                component="img"
                src={book.COVER_IMAGE || "/placeholder-book.svg"}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = "/placeholder-book.svg";
                }}
                alt={book[`TITLE_${language}`] || book.TITLE_EN}
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  transition: "transform 0.5s ease",
                }}
              />
              {/* Optional Glassmorphism overlay on image */}
              <Box
                sx={{
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: "30%",
                  background:
                    "linear-gradient(to top, rgba(0,0,0,0.4), transparent)",
                }}
              />
            </Box>

            {/* Right side: Content */}
            <Box
              sx={{
                width: "65%",
                p: 2.5,
                display: "flex",
                flexDirection: "column",
                position: "relative",
              }}
            >
              <Typography
                variant="subtitle1"
                fontWeight="700"
                sx={{
                  lineHeight: 1.3,
                  mb: 0.5,
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }}
              >
                {book[`TITLE_${language}`] || book.TITLE_EN}
              </Typography>

              <Typography
                variant="caption"
                fontWeight="600"
                sx={{
                  mb: 1,
                  display: "inline-block",
                  color: "text.secondary",
                  alignSelf: "flex-start",
                }}
              >
                {book.AUTHOR_NAME || "Unknown Author"}
              </Typography>

              <Typography
                variant="body1"
                color="text.secondary"
                sx={{
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  flexGrow: 1,
                  lineHeight: 1.5,
                  fontSize: "0.95rem",
                  opacity: 0.85,
                }}
              >
                {book[`DESCRIPTION_${language}`] ||
                  book.DESCRIPTION_EN ||
                  book[`SUMMARY_${language}`] ||
                  book.SUMMARY_EN ||
                  "No description available."}
              </Typography>

              {orientation === "row" && (
                <Box
                  className="read-more-box"
                  sx={{
                    mt: "auto",
                    display: "flex",
                    justifyContent: "flex-end",
                    opacity: 0,
                    pointerEvents: "none",
                    transition: "opacity 0.3s ease",
                  }}
                >
                  <Button
                    size="small"
                    variant="contained"
                    color="primary"
                    sx={{
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 600,
                      px: 3,
                      "&:hover": {
                        boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                      },
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate("/books/details", {
                        state: { book: { ...book } },
                      });
                    }}
                  >
                    {translate("readMore")}
                  </Button>
                </Box>
              )}
            </Box>
          </Box>
        </Fade>
      ))}
    </Box>
  );
};

export default BookCard;
